import type * as lazyIndex from './lazyIndex';
import { LazyImport, LazyModule } from 'owa-bundling';
import type { ActionSource } from 'owa-mail-store';
import type { MenuItemType } from 'owa-filterable-menu';
import type { DirectionalHint, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import type { MailboxInfo } from 'owa-client-types';
import type { MailboxType } from 'owa-graph-schema';
import type { LocalizedString } from 'owa-localize';
import type { createNewFolder } from './components/createNewFolderUtil';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "MoveToFolder"*/ './lazyIndex'),
    { name: 'MoveToFolder' }
);

const lazyGetMoveToPropertiesForContextMenu: LazyImport<
    typeof lazyIndex.getMoveToPropertiesForContextMenu,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getMoveToPropertiesForContextMenu);
const lazyGetMoveToContextCallbacks: LazyImport<
    typeof lazyIndex.getMoveToContextCallbacks,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getMoveToContextCallbacks);
const lazyGetCopyToProperties: LazyImport<typeof lazyIndex.getCopyToProperties, typeof lazyModule> =
    new LazyImport(lazyModule, m => m.getCopyToProperties);
const lazyGetCopyToContextCallbacks: LazyImport<
    typeof lazyIndex.getCopyToContextCallbacks,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getCopyToContextCallbacks);

export function getMoveToContextCallbacks(
    mailboxInfo: MailboxInfo,
    actionSource: ActionSource,
    tableViewId: string,
    shouldShowBehavior: (item: MenuItemType, mailboxInfo: MailboxInfo) => boolean,
    getTargetRowKeysFunction?: () => Promise<string[]>
): any {
    let moveToControl = undefined;
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    const getMoveToContextCallbacksFunc = lazyGetMoveToContextCallbacks.tryImportForRender();
    if (getMoveToContextCallbacksFunc) {
        moveToControl = getMoveToContextCallbacksFunc(
            mailboxInfo,
            actionSource,
            tableViewId,
            shouldShowBehavior,
            getTargetRowKeysFunction
        );
    }

    return moveToControl;
}

export function getCopyToContextCallbacks(
    tableViewId: string,
    actionSource: ActionSource,
    getTargetRowKeysFunction: () => Promise<string[]>
):
    | {
          createNewFolderCallback: typeof createNewFolder;
          onFolderClickCallback: (folderId: string, mbxInfo: MailboxInfo) => void;
          supportedMailboxTypes: MailboxType[];
          viewAllFoldersDisplayText: LocalizedString;
      }
    | undefined {
    let copyToControl = undefined;
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    const getCopyToContextCallbacksFunc = lazyGetCopyToContextCallbacks.tryImportForRender();
    if (getCopyToContextCallbacksFunc) {
        copyToControl = getCopyToContextCallbacksFunc(
            tableViewId,
            actionSource,
            getTargetRowKeysFunction
        );
    }
    return copyToControl;
}

export function getMoveToPropertiesForContextMenu(
    tableViewId: string,
    mailboxInfo: MailboxInfo,
    onDismiss: (ev?: any) => void,
    actionSource: ActionSource,
    shouldShowBehavior: (item: MenuItemType, mailboxInfo: MailboxInfo) => boolean,
    directionalHint: DirectionalHint,
    rowKeysToMove?: string[]
): IContextualMenuProps | undefined {
    let moveToControl = undefined;
    const getMoveToPropertiesForContextMenuFunc =
        /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
         * BASELINE. Do not copy and paste!
         *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
        lazyGetMoveToPropertiesForContextMenu.tryImportForRender();
    if (getMoveToPropertiesForContextMenuFunc) {
        moveToControl = getMoveToPropertiesForContextMenuFunc(
            tableViewId,
            mailboxInfo,
            onDismiss,
            actionSource,
            shouldShowBehavior,
            directionalHint,
            rowKeysToMove
        );
    }

    return moveToControl;
}

export function getCopyToProperties(
    tableViewId: string,
    onDismiss: (ev?: any) => void,
    actionSource: ActionSource
): IContextualMenuProps | undefined {
    let copyToControl = undefined;
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    const getCopyToPropertiesFunc = lazyGetCopyToProperties.tryImportForRender();
    if (getCopyToPropertiesFunc) {
        copyToControl = getCopyToPropertiesFunc(tableViewId, onDismiss, actionSource);
    }

    return copyToControl;
}
